// Chemin: C:\gestion-copro\src\pages\admin\CourriersPage.tsx
// Session 47 : ajout bouton Soumettre + tooltips explicites
// Session 75 : Sécurité suppression (corbeille visible uniquement pour Gestionnaire/Admin SaaS)
// Session 80 : Correction appels getCourriersByCopropriete (filtrage côté client)
// Session 92 : Badge classification 3 niveaux (VERT/ORANGE/ROUGE) - Gouvernance V2

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAuth } from '../../context/AuthContext';
import { 
  getCourriersByCopropriete, 
  countCourriersByStatut,
  annulerCourrier 
} from '../../services/courrier.service';
import { 
  Courrier, 
  StatutCourrier, 
  STATUT_COURRIER_LABELS, 
  TYPE_COURRIER_LABELS, 
  MODE_ENVOI_LABELS,
  NiveauClassification,
  NIVEAU_CLASSIFICATION_SHORT_LABELS,
  NIVEAU_CLASSIFICATION_DOT_COLORS,
  NIVEAU_CLASSIFICATION_DESCRIPTIONS
} from '../../types/courrier.types';
import { Timestamp } from 'firebase/firestore';
import { Eye, Edit2, Send, Trash2, Plus, Mail, Calendar, User, Paperclip } from 'lucide-react';

// Types locaux
type OngletStatut = StatutCourrier | 'tous';

interface StatsStatuts {
  brouillon: number;
  en_validation: number;
  valide: number;
  envoye: number;
  annule: number;
  rejete: number;
  total: number;
}

const CourriersPage: React.FC = () => {
  const navigate = useNavigate();
  const { coproprieteId, copropriete } = useCopropriete();
  const { firebaseUser, userProfile } = useAuth();
  
  // Session 75 : Helper pour la permission de suppression
  // Seuls Gestionnaire et Admin SaaS peuvent supprimer des courriers
  const canDeleteCourrier = userProfile?.isGestionnaireSaas === true || 
                            userProfile?.role === 'gestionnaire' || 
                            userProfile?.role === 'admin_saas';
  
  // États
  const [courriers, setCourriers] = useState<Courrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ongletActif, setOngletActif] = useState<OngletStatut>('tous');
  const [stats, setStats] = useState<StatsStatuts | null>(null);
  const [courrierASupprimer, setCourrierASupprimer] = useState<Courrier | null>(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);

  // Chargement des courriers
  useEffect(() => {
    const chargerDonnees = async () => {
      if (!coproprieteId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Charger les stats
        const statsResult = await countCourriersByStatut(coproprieteId);
        setStats({
          ...statsResult,
          total: Object.values(statsResult).reduce((a, b) => a + b, 0)
        });
        
        // Charger les courriers (tous) puis filtrer côté client
        const statutFiltre = ongletActif === 'tous' ? undefined : ongletActif;
        const allCourriers = await getCourriersByCopropriete(coproprieteId);
        const courriersResult = statutFiltre 
          ? allCourriers.filter(c => c.statut === statutFiltre)
          : allCourriers;
        setCourriers(courriersResult);
      } catch (err) {
        console.error('Erreur chargement courriers:', err);
        setError('Impossible de charger les courriers');
      } finally {
        setLoading(false);
      }
    };
    
    chargerDonnees();
  }, [coproprieteId, ongletActif]);

  // Formatage date
  const formatDate = (timestamp: Timestamp | undefined): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate();
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Couleur du badge statut
  const getStatutColor = (statut: StatutCourrier): string => {
    const colors: Record<StatutCourrier, string> = {
      brouillon: 'bg-gray-100 text-gray-700',
      en_validation: 'bg-yellow-100 text-yellow-700',
      valide: 'bg-green-100 text-green-700',
      envoye: 'bg-blue-100 text-blue-700',
      recu: 'bg-cyan-100 text-cyan-700',
      annule: 'bg-red-100 text-red-700',
      rejete: 'bg-orange-100 text-orange-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  // Session 92 : Récupérer le niveau de classification d'un courrier
  const getNiveauClassification = (courrier: Courrier): NiveauClassification | undefined => {
    return courrier.niveauClassification 
      || courrier.analyseSentinelle?.niveauClassification;
  };

  // Libellé du type de destinataire
  const getDestinataireLabel = (courrier: Courrier): string => {
    const typeLabels: Record<string, string> = {
      syndic: 'Syndic',
      promoteur: 'Promoteur',
      entreprise: 'Entreprise',
      coproprietaire: 'Copropriétaire',
      autre: 'Autre'
    };
    const typeLabel = typeLabels[courrier.destinataire.type] || '';
    return typeLabel ? `${courrier.destinataire.nom} (${typeLabel})` : courrier.destinataire.nom;
  };

  // Navigation
  const handleNouveauCourrier = () => {
    navigate('/admin/courriers/nouveau');
  };

  const handleVoirCourrier = (courrierId: string) => {
    navigate(`/admin/courriers/${courrierId}`);
  };

  const handleModifierCourrier = (courrierId: string) => {
    navigate(`/admin/courriers/${courrierId}/modifier`);
  };

  // Session 47 : Soumettre directement depuis la liste
  // Navigue vers le détail avec paramètre pour déclencher l'analyse SCUD
  const handleSoumettreCourrier = (courrierId: string) => {
    navigate(`/admin/courriers/${courrierId}?action=soumettre`);
  };

  // Suppression (annulation)
  const handleConfirmerSuppression = async () => {
    if (!courrierASupprimer || !firebaseUser || !userProfile) return;
    
    setSuppressionEnCours(true);
    try {
      await annulerCourrier(
        courrierASupprimer.id,
        firebaseUser.uid,
        `${userProfile.prenom} ${userProfile.nom}`,
        'Annulé par l\'utilisateur'
      );
      
      // Recharger la liste (tous) puis filtrer côté client
      const statutFiltre = ongletActif === 'tous' ? undefined : ongletActif;
      const allCourriers = await getCourriersByCopropriete(coproprieteId!);
      const courriersResult = statutFiltre 
        ? allCourriers.filter(c => c.statut === statutFiltre)
        : allCourriers;
      setCourriers(courriersResult);
      
      // Recharger les stats
      const statsResult = await countCourriersByStatut(coproprieteId!);
      setStats({
        ...statsResult,
        total: Object.values(statsResult).reduce((a, b) => a + b, 0)
      });
      
      setCourrierASupprimer(null);
    } catch (err) {
      console.error('Erreur annulation courrier:', err);
      setError('Impossible d\'annuler le courrier');
    } finally {
      setSuppressionEnCours(false);
    }
  };

  // Onglets
  const onglets: { id: OngletStatut; label: string; count: number }[] = [
    { id: 'tous', label: 'Tous', count: stats?.total || 0 },
    { id: 'brouillon', label: 'Brouillons', count: stats?.brouillon || 0 },
    { id: 'en_validation', label: 'En validation', count: stats?.en_validation || 0 },
    { id: 'valide', label: 'Validés', count: stats?.valide || 0 },
    { id: 'envoye', label: 'Envoyés', count: stats?.envoye || 0 }
  ];

  // Rendu
  if (!coproprieteId) {
    return (
      <div className="p-6">
        <p className="text-red-600">Aucune copropriété sélectionnée</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Courriers</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez les courriers sortants du conseil syndical
          </p>
        </div>
        <button
          onClick={handleNouveauCourrier}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouveau courrier
        </button>
      </div>

      {/* Onglets */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          {onglets.map((onglet) => (
            <button
              key={onglet.id}
              onClick={() => setOngletActif(onglet.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                ongletActif === onglet.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {onglet.label}
              {onglet.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  ongletActif === onglet.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {onglet.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Erreur */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Chargement */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des courriers...</span>
        </div>
      ) : courriers.length === 0 ? (
        /* Liste vide */
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Mail className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun courrier</h3>
          <p className="mt-2 text-gray-500">
            {ongletActif === 'tous' 
              ? 'Commencez par créer votre premier courrier.'
              : `Aucun courrier avec le statut "${STATUT_COURRIER_LABELS[ongletActif as StatutCourrier]}".`
            }
          </p>
          {ongletActif === 'tous' && (
            <button
              onClick={handleNouveauCourrier}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Créer un courrier
            </button>
          )}
        </div>
      ) : (
        /* Liste des courriers */
        <div className="space-y-4">
          {courriers.map((courrier) => {
            // Session 92 : Récupérer le niveau de classification
            const niveau = getNiveauClassification(courrier);
            
            return (
              <div
                key={courrier.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  {/* Infos principales */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {/* Session 92 : Pastille de classification colorée */}
                      {niveau && (
                        <span
                          className="relative group flex items-center gap-1.5"
                          title={NIVEAU_CLASSIFICATION_DESCRIPTIONS[niveau]}
                        >
                          <span className={`inline-block w-3 h-3 rounded-full ${NIVEAU_CLASSIFICATION_DOT_COLORS[niveau]}`} />
                          <span className={`text-xs font-bold ${
                            niveau === 'vert' ? 'text-green-700' 
                            : niveau === 'orange' ? 'text-orange-700' 
                            : 'text-red-700'
                          }`}>
                            {NIVEAU_CLASSIFICATION_SHORT_LABELS[niveau]}
                          </span>
                          {/* Tooltip au survol */}
                          <span className="absolute left-0 top-full mt-1 z-10 hidden group-hover:block w-64 p-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg">
                            {NIVEAU_CLASSIFICATION_DESCRIPTIONS[niveau]}
                            {courrier.niveauSurclasse && (
                              <span className="block mt-1 text-amber-600 font-medium">
                                Surclassé par le coordinateur
                              </span>
                            )}
                          </span>
                        </span>
                      )}
                      <span className="text-sm font-mono text-gray-500">
                        {courrier.numero}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatutColor(courrier.statut)}`}>
                        {STATUT_COURRIER_LABELS[courrier.statut]}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                        {TYPE_COURRIER_LABELS[courrier.type]}
                      </span>
                    </div>
                    
                    <h3 className="font-medium text-gray-900 mb-1">
                      {courrier.objet}
                    </h3>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {getDestinataireLabel(courrier)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(courrier.dateCreation)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {MODE_ENVOI_LABELS[courrier.modeEnvoi]}
                      </span>
                      {courrier.piecesJointes.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Paperclip className="w-4 h-4" />
                          {courrier.piecesJointes.length} pièce(s)
                        </span>
                      )}
                    </div>

                    {/* Indicateur de votes si en validation (ROUGE uniquement) */}
                    {courrier.statut === 'en_validation' && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-yellow-700 bg-yellow-50 px-2 py-1 rounded">
                          {niveau === 'rouge' 
                            ? `Votes : ${Object.values(courrier.validations || {}).filter(v => v.statut === 'approuve').length} approbation(s) — 1 requise`
                            : `Votes : ${Object.keys(courrier.validations || {}).length} / ${courrier.votesRequis} requis`
                          }
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-4">
                    {/* Bouton Voir - toujours visible */}
                    <button
                      onClick={() => handleVoirCourrier(courrier.id)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Voir le détail du courrier"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    
                    {/* Actions pour brouillons uniquement */}
                    {courrier.statut === 'brouillon' && (
                      <>
                        {/* Bouton Modifier */}
                        <button
                          onClick={() => handleModifierCourrier(courrier.id)}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Modifier le contenu du courrier"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        
                        {/* Session 47 : Bouton Soumettre */}
                        <button
                          onClick={() => handleSoumettreCourrier(courrier.id)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Soumettre à validation du conseil syndical (analyse SENTINELLE)"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                        
                        {/* Session 75 : Bouton Supprimer visible uniquement pour Gestionnaire/Admin SaaS */}
                        {canDeleteCourrier && (
                          <button
                            onClick={() => setCourrierASupprimer(courrier)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Annuler et supprimer ce courrier"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {courrierASupprimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Annuler ce courrier ?
            </h3>
            <p className="text-gray-600 mb-4">
              Le courrier "{courrierASupprimer.objet}" sera marqué comme annulé. 
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCourrierASupprimer(null)}
                disabled={suppressionEnCours}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Non, garder
              </button>
              <button
                onClick={handleConfirmerSuppression}
                disabled={suppressionEnCours}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {suppressionEnCours ? 'Annulation...' : 'Oui, annuler'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourriersPage;
